import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { Flex, Heading, Box, Text } from 'rebass';
import { FiMail } from 'react-icons/fi';

import { post } from 'utils/api';
import Wrapper from 'babys-first-year/Wrapper';
import { FieldText, label } from 'babys-first-year/SignUp';

function ForgotPasswordInstructions({ email }) {
  return (
    <Flex bg="purples.1" p={4} sx={{ borderRadius: 'medium' }}>
      <Text as={FiMail} color="purples.4" size={72} mr={3} />
      <Box>
        <Heading color="purples.7" pb={1}>
          Check Your Email!
        </Heading>
        <Text color="purples.8">If an account exists for this email, we've sent you a password reset link. Please check your inbox and spam folder.</Text>
      </Box>
    </Flex>
  );
}

// Standalone "request a password reset" page, its own route (/password/forgot) — mounted
// outside <Auth> exactly like the sibling /password/:id/reset page (auth/reset.js), so it
// renders for anonymous visitors and survives a refresh/direct link. Previously this was
// local component state toggled inside LoginSignup.js/SignUp.js, which reset on refresh
// and had no shareable URL. Submits to the same /v3/password_resets endpoint those forms
// always used — only where it lives changed, not the request/response contract.
const ForgotPasswordPage = () => {
  const { push } = useHistory();
  const [submittedEmail, setSubmittedEmail] = useState(null);

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      await post('/v3/password_resets', values);
      setSubmittedEmail(values.email);
    } finally {
      setSubmittedEmail(values.email);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ email: '' }}
      validationSchema={Yup.object().shape({ email: Yup.string().email().required() })}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, handleSubmit }) => (
        <Form>
          <Wrapper
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting || !!submittedEmail}
            heading="Forgot Your Password?"
            subHeading="Enter your email and we’ll send you instructions to reset it."
            percentComplete={0}
            bg="aliceBlue"
            nextButtonText="Reset password"
            buttonSx={{ ml: 'auto', width: 'max-content' }}
            backText="Back to login"
            onBack={() => push('/')}
          >
            <Box width="100%" maxWidth="600px" mx="auto">
              {submittedEmail ? (
                <ForgotPasswordInstructions email={submittedEmail} />
              ) : (
                <Box>
                  <Text
                    color="codGrey"
                    textAlign="left"
                    fontFamily="Bitter"
                    fontSize={[24, 28]}
                    lineHeight={['34px', '38px']}
                    letterSpacing="-0.6"
                    mt={2}
                    mb={1}
                  >
                    Reset your password
                  </Text>
                  <FieldText
                    name="email"
                    label={label('Enter your email address and we\'ll send you instructions to reset your password', true)}
                    width="100%"
                    placeholder="Enter your email address"
                  />
                </Box>
              )}
            </Box>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

export default ForgotPasswordPage;
